// src/pages/MovieDetails.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchMovieDetails } from '../services/api';
import { useScrollRestoration } from '../hooks/useNavigationState';
import { useSearch } from '../context/SearchContext';
import IMDbIcon from '../images/imdb-icon.svg';
import TMDBIcon from '../images/tmdb-icon.svg';
import platformIcons from '../data/platformIcons';
import { formatRuntime, formatReleaseDate, formatFinancialValue, calculateROI } from '../utils/formatters';
import './MovieDetails.css';

const formatListData = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data.filter(Boolean);
  if (typeof data === 'string') {
    return data.split(',').map(item => item.trim()).filter(Boolean);
  }
  return [];
};

const getStreamingPlatformsForCountry = (platforms, countryCode = 'FR') => {
  if (!platforms || !platforms[countryCode]) return [];
  return platforms[countryCode];
};

const MovieDetails = () => {
  useScrollRestoration();
  const navigate = useNavigate();
  const { id } = useParams();
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { setSearchQuery, setSearchField } = useSearch();
  const fetchingRef = useRef(false);

  // New state variables for "Show More" functionality
  const [showFullCast, setShowFullCast] = useState(false);
  const [showFullProducers, setShowFullProducers] = useState(false);
  const [showFullCompanies, setShowFullCompanies] = useState(false);
  const [showFullWriters, setShowFullWriters] = useState(false);
  const [showFullCountries, setShowFullCountries] = useState(false);

  useEffect(() => {
    const loadMovieDetails = async () => {
      if (fetchingRef.current) return;
      try {
        fetchingRef.current = true;
        setLoading(true);
        setError(null);

        const data = await fetchMovieDetails(id);
        setMovie(data);
        if (data?.title) document.title = data.title;
      } catch (err) {
        setError('Failed to load movie details');
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    };
    loadMovieDetails();

    // Cleanup
    return () => {
      fetchingRef.current = false;
      document.title = 'MovieMate';
    };
  }, [id]);

  const navigateWithFilter = (query, field) => {
    setSearchQuery(query);
    setSearchField(field);
    navigate('/');
  };

  // if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;
  // if (!movie) return <div className="not-found">Movie not found</div>;
  if (loading) return null;
  if (error) return <div className="error">{error}</div>;
  if (!movie) return <div className="not-found">Movie not found</div>;

  // Use cast_details (array of objects), default to []
  const castDetails = Array.isArray(movie.cast_details) ? movie.cast_details : [];

  // Decide how many you show by default
  const displayedCast = showFullCast ? castDetails : castDetails.slice(0, 6);


  // Check if streaming platforms exist
  const hasStreamingPlatforms = movie?.streaming_platforms &&
    Object.values(movie.streaming_platforms).some(platforms =>
      Array.isArray(platforms) && platforms.length > 0);

  // Utility to render a field if it exists
  const renderSection = (title, content) => {
    if (!content) return null;
    return (
      <div className="movie-detail-section">
        <h2 className="movie-detail-section-title">{title}</h2>
        {content}
      </div>
    );
  };

  const ImdbLink = ({ imdbId }) => {
    if (!imdbId) return null;
    return (
      <a
        href={`https://www.imdb.com/title/${imdbId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="imdb-link"
        title="View on IMDb"
      >
        <img src={IMDbIcon} alt="IMDb" className="movie-detail-imdb-icon" />
      </a>
    );
  };

  const TmdbLink = ({ movieId }) => {
    return (
      <a
        href={`https://www.themoviedb.org/movie/${movieId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="tmdb-link"
        title="View on TMDB"
      >
        <img src={TMDBIcon} alt="TMDB" className="movie-detail-tmdb-icon" />
      </a>
    );
  };

  return (
    <div className="movie-detail-page">
      <div className="movie-detail-hero">
        {/* Backdrop Image */}
        <div className="movie-detail-backdrop">
          <img src={movie.backdrop_url} alt="" className="movie-detail-backdrop-image" />
          <div className="movie-detail-backdrop-overlay" />
        </div>

        <div className="movie-detail-content">
          <div className="movie-detail-poster-container">
            <img src={movie.poster_url} alt={movie.title} className="movie-detail-poster" />
          </div>
          <div className="movie-detail-info-wrapper">
            <div className="movie-detail-info">
              <h1 className="movie-detail-title">{movie.title}</h1>

              {movie.director && (
                <p
                  className="movie-detail-director"
                  onClick={() => navigateWithFilter(movie.director, 'director')}
                  title={`Search for more movies by ${movie.director}`}
                >
                  {movie.director}
                </p>
              )}

              <div className="movie-detail-meta">
                <span>{formatReleaseDate(movie.release_date)}</span>
                <span>•</span>
                <span>{formatRuntime(movie.runtime)}</span>
                <span>•</span>
                <div className="movie-detail-imdb">
                  <ImdbLink imdbId={movie.imdb_id} />
                  <span>{movie.imdb_rating || 'N/A'}</span>
                </div>
                <span>•</span>
                <TmdbLink movieId={movie.id} />
              </div>

              {formatListData(movie.genres).length > 0 && (
                <div className="movie-detail-genres">
                  {formatListData(movie.genres).join(', ')}
                </div>
              )}

              <p className="movie-detail-overview">
                {movie.overview || 'No overview available.'}
              </p>

              <div className="movie-detail-info-grid">
                {renderSection("Crew", (
                  <>
                    {/* Director */}
                    {movie.director && (
                      <div className="movie-detail-crew-item">
                        <strong>Director:</strong>{" "}
                        {formatListData(movie.director).map((dir, index) => (
                          <span
                            key={index}
                            className="clickable-chip"
                            onClick={() => navigateWithFilter(dir, 'director')}
                          >
                            {dir}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Writers */}
                    {movie.writers && (
                      <div className="movie-detail-crew-item">
                        <strong>Writers:</strong>{" "}
                        {formatListData(movie.writers)
                          .slice(0, showFullWriters ? undefined : 3)
                          .map((writer, index) => (
                            <span
                              key={index}
                              className="clickable-chip"
                              onClick={() => navigateWithFilter(writer, 'writers')}
                            >
                              {writer}
                            </span>
                          ))}
                        {formatListData(movie.writers).length > 3 && (
                          <button className="show-more-button-details" onClick={() => setShowFullWriters(!showFullWriters)}>
                            {showFullWriters ? 'Show Less' : 'Show More'}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Cinematography */}
                    {movie.director_of_photography && (
                      <div className="movie-detail-crew-item">
                        <strong>Cinematography:</strong>{" "}
                        {formatListData(movie.director_of_photography).map((dop, index) => (
                          <span
                            key={index}
                            className="clickable-chip"
                            onClick={() => navigateWithFilter(dop, 'director_of_photography')}
                          >
                            {dop}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Composer */}
                    {movie.music_composer && (
                      <div className="movie-detail-crew-item">
                        <strong>Composer:</strong>{" "}
                        {formatListData(movie.music_composer).map((comp, index) => (
                          <span
                            key={index}
                            className="clickable-chip"
                            onClick={() => navigateWithFilter(comp, 'music_composer')}
                          >
                            {comp}
                          </span>
                        ))}
                      </div>
                    )}


                  </>
                ))}

                {/* Cast using cast_details */}
                {renderSection("Cast", (
                  <div className="movie-detail-cast">
                    {displayedCast.map((member, index) => (
                      <span
                        key={index}
                        className="clickable-chip tooltip"
                        onClick={() => navigateWithFilter(member.name, 'cast_members')}
                      >
                        {member.name}
                        {member.character && (
                          <span className="tooltiptext">{member.character}</span>
                        )}
                      </span>
                    ))}
                    {castDetails.length > 6 && (
                      <button
                        className="show-more-button-details"
                        onClick={() => setShowFullCast(!showFullCast)}
                      >
                        {showFullCast ? 'Show Less' : 'Show More'}
                      </button>
                    )}
                  </div>
                ))}

                {/* Production Section */}

                {renderSection("Production", (
                  <>
                    <div className="movie-detail-production-item">
                      <strong>Countries:</strong>{" "}
                      {formatListData(movie.production_countries)
                        .slice(0, showFullCountries ? undefined : 3)
                        .map((country, index) => (
                          <span
                            key={index}
                            className="clickable-chip"
                            onClick={() => navigateWithFilter(country, 'production_countries')}
                          >
                            {country}
                          </span>
                        ))}
                      {formatListData(movie.production_countries).length > 3 && (
                        <button className="show-more-button-details" onClick={() => setShowFullCountries(!showFullCountries)}>
                          {showFullCountries ? 'Show Less' : 'Show More'}
                        </button>
                      )}
                    </div>
                    <div className="movie-detail-production-item">
                      <strong>Companies:</strong>{" "}
                      {formatListData(movie.production_companies)
                        .slice(0, showFullCompanies ? undefined : 3)
                        .map((company, index) => (
                          <span
                            key={index}
                            className="clickable-chip"
                            onClick={() => navigateWithFilter(company, 'production_companies')}
                          >
                            {company}
                          </span>
                        ))}
                      {formatListData(movie.production_companies).length > 3 && (
                        <button className="show-more-button-details" onClick={() => setShowFullCompanies(!showFullCompanies)}>
                          {showFullCompanies ? 'Show Less' : 'Show More'}
                        </button>
                      )}
                    </div>
                    <div className="movie-detail-production-item">
                      <strong>Producers:</strong>{" "}
                      {formatListData(movie.producers)
                        .slice(0, showFullProducers ? undefined : 3)
                        .map((prod, index) => (
                          <span
                            key={index}
                            className="clickable-chip"
                            onClick={() => navigateWithFilter(prod, 'producers')}
                          >
                            {prod}
                          </span>
                        ))}
                      {formatListData(movie.producers).length > 3 && (
                        <button className="show-more-button-details" onClick={() => setShowFullProducers(!showFullProducers)}>
                          {showFullProducers ? 'Show Less' : 'Show More'}
                        </button>
                      )}
                    </div>
                  </>
                ))}

                {/* Finance */}
                {renderSection("Finance", (
                  <div className="movie-detail-financial">
                    <div className="revenue">
                      Revenue: <span className="value">{formatFinancialValue(movie.revenue)}</span>
                    </div>
                    <div className="budget">
                      Budget: <span className="value">{formatFinancialValue(movie.budget)}</span>
                    </div>
                    <div className="roi">
                      ROI: <span className="value">{calculateROI(movie.revenue, movie.budget)}</span>
                    </div>
                  </div>
                ))}

                {/* Streaming Platforms */}
                {hasStreamingPlatforms &&
                  renderSection("Streaming on", (
                    <div className="movie-detail-platforms">
                      {getStreamingPlatformsForCountry(movie.streaming_platforms).map(platform => (
                        <div key={platform} className="movie-detail-platform-item">
                          {platformIcons[platform] ? (
                            <img
                              src={platformIcons[platform]}
                              alt={platform}
                              className="movie-detail-platform-icon"
                              title={platform}
                            />
                          ) : (
                            <span className="movie-detail-platform-name">{platform}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovieDetails;
